import React, { useMemo, useState } from 'react'
import {
  Badge,
  DescriptionList,
  SelectableList,
  SELECTABLE_LIST_DEMO_ITEMS,
  SELECTABLE_LIST_PREVIEW_STATE_LABELS,
  SELECTABLE_LIST_PREVIEW_STATES,
  type SelectableListItem,
  type SelectableListPreviewState,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './SelectableListShowcase.css'

const CLAIMS_ITEMS: SelectableListItem[] = [
  {
    id: '1',
    primary: 'CLM-10482',
    secondary: '3/12/2026',
    meta: '$1,250.00',
    status: <Badge label="Posted" mode="tinted" color="vivid-green" />,
  },
  {
    id: '2',
    primary: 'CLM-10491',
    secondary: '3/14/2026',
    meta: '$840.00',
    status: <Badge label="Pending" mode="tinted" color="yellow" />,
  },
  {
    id: '3',
    primary: 'CLM-10502',
    secondary: '3/18/2026',
    meta: '$2,110.00',
    status: <Badge label="Processing" mode="tinted" color="blue" />,
  },
]

const CLAIM_DETAILS: Record<
  string,
  { amount: string; status: string; paid: string }
> = {
  '1': { amount: '$1,250.00', status: 'Posted', paid: '$980.00' },
  '2': { amount: '$840.00', status: 'Pending', paid: '$0.00' },
  '3': { amount: '$2,110.00', status: 'Processing', paid: '$1,400.00' },
}

const SelectableListShowcase: React.FC = () => {
  const [title, setTitle] = useState('Claim list')
  const [showTitle, setShowTitle] = useState(true)
  const [showTitleMeta, setShowTitleMeta] = useState(true)
  const [selectedId, setSelectedId] = useState(CLAIMS_ITEMS[0].id)
  const [highlightedState, setHighlightedState] =
    useState<SelectableListPreviewState>('default')

  const selectedClaim =
    CLAIMS_ITEMS.find((item) => item.id === selectedId) ?? CLAIMS_ITEMS[0]
  const selectedDetails =
    CLAIM_DETAILS[selectedId] ?? CLAIM_DETAILS[CLAIMS_ITEMS[0].id]

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'selectable-list-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="selectable-list-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as SelectableListPreviewState,
                  )
                }
              >
                {SELECTABLE_LIST_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {SELECTABLE_LIST_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'selectable-list-title',
            label: 'Title',
            control: (
              <input
                id="selectable-list-title"
                className="ui-field-control"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={!showTitle}
              />
            ),
          },
          {
            id: 'selectable-list-show-title',
            label: 'Show title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selectable-list-show-title"
                  type="checkbox"
                  checked={showTitle}
                  onChange={(event) => setShowTitle(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'selectable-list-show-meta',
            label: 'Show title meta',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="selectable-list-show-meta"
                  type="checkbox"
                  checked={showTitleMeta}
                  onChange={(event) => setShowTitleMeta(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [highlightedState, showTitle, showTitleMeta, title],
  )

  const titleMetaLabel = `${CLAIMS_ITEMS.length} Claims`

  return (
    <>
      <UiPreviewCanvas
        title="Selectable list"
        description="Master list with header and rich rows (primary / secondary / meta / status) — as used for Claim list in Claims → Tracking → Transaction info."
        stageClassName="ui-preview__stage--wide selectable-list-showcase__stage-wrap"
      >
        <div className={`selectable-list-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Click a claim to update the description list on the right.
            </p>
            <div className={`selectable-list-showcase__live-layout`}>
              {highlightedState === 'default' ? (
                <SelectableList
                  title={showTitle ? title : undefined}
                  titleMeta={showTitleMeta ? titleMetaLabel : undefined}
                  items={CLAIMS_ITEMS}
                  value={selectedId}
                  onChange={setSelectedId}
                />
              ) : (
                <SelectableList previewState={highlightedState} />
              )}
              <div className="selectable-list-showcase__live-detail">
                <DescriptionList
                  groups={[
                    [
                      {
                        label: 'Claim ID',
                        value: selectedClaim.primary,
                      },
                      {
                        label: 'Claim submitted',
                        value: selectedClaim.secondary,
                      },
                      {
                        label: 'Claim amount',
                        value: selectedDetails.amount,
                      },
                    ],
                    [
                      {
                        label: 'Claim status',
                        value: (
                          <Badge
                            label={selectedDetails.status}
                            mode="tinted"
                            color={
                              selectedDetails.status === 'Posted'
                                ? 'vivid-green'
                                : selectedDetails.status === 'Pending'
                                  ? 'yellow'
                                  : 'blue'
                            }
                          />
                        ),
                      },
                      {
                        label: 'Paid amount',
                        value: selectedDetails.paid,
                      },
                    ],
                  ]}
                  variant="panel"
                />
              </div>
            </div>
          </section>

          <section className="selectable-list-showcase__demo">
            <h3 className="ui-showcase-live__title">
              Claims — Transaction info sidebar
            </h3>
            <p className="ui-showcase-live__note">
              Standalone claim list matching the modal sidebar (~250px).
            </p>
            <SelectableList
              title="Claim list"
              titleMeta="3 Claims"
              items={CLAIMS_ITEMS}
              defaultValue="1"
            />
          </section>

          <section className="selectable-list-showcase__matrix">
            {SELECTABLE_LIST_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`selectable-list-showcase__row${
                  highlightedState === previewState
                    ? ' selectable-list-showcase__row--selected'
                    : ''
                }`}
              >
                <h4 className="selectable-list-showcase__label">
                  {SELECTABLE_LIST_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <SelectableList
                  previewState={previewState}
                  items={
                    previewState === 'default'
                      ? SELECTABLE_LIST_DEMO_ITEMS
                      : undefined
                  }
                />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="SelectableList"
        sections={propertySections}
      />
    </>
  )
}

export default SelectableListShowcase
